import React, { memo, useEffect, useMemo, useState } from 'react';
import { Download, Loader2, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../../utils/axiosConfig';
import {
    Avatar, Cargando, DIAS_LETRA, ESTADOS, Flotante, LEYENDA, Leyenda, NombrePersona, PERIODOS, Segmentado, aFecha,
    botonClaro, campo, diaSemana, display, fechaLarga, horaAmPm, horas, iso, minutosTexto, panel, partesHora,
    periodo, useAbrirFicha,
} from '../ui';

const ORDENES = {
    nombre: (a, b) => (a.nombre || '').localeCompare(b.nombre || ''),
    retardos: (a, b) => b.totales.minutos_retardo - a.totales.minutos_retardo,
    ausencias: (a, b) => b.totales.ausencias - a.totales.ausencias,
    horas: (a, b) => b.totales.horas - a.totales.horas,
    extra: (a, b) => b.totales.extra - a.totales.extra,
};

// "+3h" o "+45m": corto, para que entre en la celda
const extraCorta = (min) => (min >= 60 ? `+${Math.round(min / 60)}h` : `+${min}m`);

const textoCelda = (c) => {
    if (!c) return '';
    if (c.estado === 'libre') return c.justificacion || 'No laborable';
    if (c.estado === 'ausente') return 'Faltó';
    if (c.estado === 'justificada') return c.justificacion || 'Justificado';
    if (c.estado === 'en_curso' && !c.entrada) return 'Todavía no marca';
    const partes = [];
    if (c.entrada) partes.push(`Entró ${horaAmPm(c.entrada)}`);
    if (c.salida) partes.push(`salió ${horaAmPm(c.salida)}`);
    return partes.join(', ');
};

// Una celda del día. En períodos cortos lleva la hora de entrada y salida; en largos, solo el color.
const Celda = ({ celda, ancha, onEncima, onSalir, onAbrir }) => {
    if (!celda) return <div className={ancha ? 'h-11' : 'h-7'} />;
    const estilo = ESTADOS[celda.estado] || ESTADOS.libre;
    const entrada = partesHora(celda.entrada);
    const salida = partesHora(celda.salida);
    return (
        <button
            type="button" onClick={onAbrir} onMouseEnter={onEncima} onMouseLeave={onSalir} onFocus={onEncima} onBlur={onSalir}
            aria-label={`${estilo.nombre}. ${textoCelda(celda)}`}
            className={`relative w-full rounded-md ${estilo.celda} ${ancha ? 'h-11 px-1.5 text-left' : 'h-7'} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-1 hover:brightness-95`}
        >
            {ancha && entrada && (
                <span className="block text-[11px] leading-tight tabular-nums">
                    <span className="font-semibold">{entrada.hora}</span> <span className="opacity-75">{entrada.sufijo}</span>
                    {salida && (
                        <span className="block">
                            <span className="font-semibold">{salida.hora}</span> <span className="opacity-75">{salida.sufijo}</span>
                        </span>
                    )}
                </span>
            )}
            {ancha && !entrada && celda.estado !== 'libre' && (
                <span className="block text-[11px] font-medium leading-tight truncate">
                    {celda.estado === 'en_curso' ? 'Sin marcar' : ESTADOS[celda.estado]?.nombre}
                </span>
            )}
            {celda.aviso && <span className="absolute bottom-1 right-1 w-1.5 h-1.5 rounded-full bg-rose-500 ring-1 ring-white" />}
            {celda.extra_min > 0 && (ancha ? (
                <span className="absolute top-1 right-1 px-1 h-4 rounded bg-cyan-400 text-[10px] font-bold leading-4 text-slate-900 tabular-nums">
                    {extraCorta(celda.extra_min)}
                </span>
            ) : (
                <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-cyan-300 ring-1 ring-white" />
            ))}
        </button>
    );
};

const Fila = memo(({ persona, dias, ancha, columnas, onEncima, onSalir, mesFicha }) => {
    const abrir = useAbrirFicha();
    const t = persona.totales;
    return (
        <div className="contents group">
            <div className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 flex items-center gap-2.5 pr-3 pl-4 py-1.5 border-b border-slate-100">
                <Avatar persona={persona} size={28} />
                <div className="min-w-0">
                    <NombrePersona persona={persona} className="block text-sm font-medium text-slate-900 truncate max-w-[10.5rem]" />
                    <p className="text-[11px] text-slate-400 truncate max-w-[10.5rem]">{persona.departamento}</p>
                </div>
            </div>
            {dias.map((d) => (
                <div key={d} className="px-[2px] py-1.5 border-b border-slate-100 group-hover:bg-slate-50 flex items-center">
                    <Celda
                        celda={persona.celdas[d]} ancha={ancha}
                        onEncima={(e) => onEncima(e, persona, d)} onSalir={onSalir}
                        onAbrir={() => abrir(persona.empleado_id, mesFicha || d.slice(0, 7))}
                    />
                </div>
            ))}
            <div className="sticky right-0 z-10 bg-white group-hover:bg-slate-50 border-b border-l border-slate-100 grid text-sm tabular-nums" style={{ gridTemplateColumns: columnas }}>
                <span className="px-2 py-2 text-right text-slate-900 font-medium">{horas(t.horas)}</span>
                <span className="px-2 py-2 text-right text-slate-400">{horas(t.programadas)}</span>
                <span className={`px-2 py-2 text-right ${t.extra ? 'text-cyan-700 font-medium' : 'text-slate-300'}`}
                    title={t.extra ? `${minutosTexto(t.minutos_extra)} en ${t.dias_extra} ${t.dias_extra === 1 ? 'día' : 'días'}, aparte de las horas` : undefined}>
                    {horas(t.extra)}
                </span>
                <span className={`px-2 py-2 text-right ${t.retardos ? 'text-amber-600 font-medium' : 'text-slate-300'}`} title={t.retardos ? minutosTexto(t.minutos_retardo) : undefined}>{t.retardos}</span>
                <span className={`px-2 py-2 text-right ${t.ausencias ? 'text-rose-600 font-medium' : 'text-slate-300'}`}>{t.ausencias}</span>
            </div>
        </div>
    );
});

const PeriodoVista = () => {
    const [clave, setClave] = useState('semana');
    const [rango, setRango] = useState(periodo('semana'));
    const [sedeId, setSedeId] = useState('');
    const [deptoId, setDeptoId] = useState('');
    const [texto, setTexto] = useState('');
    const [busqueda, setBusqueda] = useState('');
    const [soloNovedades, setSoloNovedades] = useState(false);
    const [orden, setOrden] = useState('nombre');
    const [filtros, setFiltros] = useState({ sedes: [], departamentos: [] });
    const [datos, setDatos] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState(null);
    const [encima, setEncima] = useState(null);
    const [bajando, setBajando] = useState(null);

    useEffect(() => {
        axiosInstance.get('/api/asistencia/filtros').then(({ data }) => setFiltros(data)).catch(() => {});
    }, []);
    useEffect(() => {
        const t = setTimeout(() => setBusqueda(texto.trim()), 300);
        return () => clearTimeout(t);
    }, [texto]);

    const [desde, hasta] = rango;
    const params = { desde, hasta, sede_id: sedeId || undefined, departamento_id: deptoId || undefined, q: busqueda || undefined };

    useEffect(() => {
        let vivo = true;
        setCargando(true);
        axiosInstance.get('/api/asistencia/planilla', { params })
            .then(({ data }) => { if (vivo) { setDatos(data); setError(null); } })
            .catch((e) => { if (vivo) setError(e.response?.data?.error || 'No se pudo armar la planilla.'); })
            .finally(() => vivo && setCargando(false));
        return () => { vivo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [desde, hasta, sedeId, deptoId, busqueda]);

    const elegirPeriodo = (id) => { setClave(id); setRango(periodo(id)); };
    const cambiarFecha = (i, valor) => {
        if (!valor) return;
        setClave(null);
        setRango((r) => (i === 0 ? [valor, r[1] < valor ? valor : r[1]] : [r[0] > valor ? valor : r[0], valor]));
    };

    const personas = useMemo(() => {
        if (!datos) return [];
        let lista = datos.personas;
        if (soloNovedades) {
            lista = lista.filter((p) => p.totales.retardos || p.totales.ausencias || p.totales.avisos || p.totales.salidas_tempranas);
        }
        return [...lista].sort(ORDENES[orden]);
    }, [datos, soloNovedades, orden]);

    const conteo = useMemo(() => {
        const c = {};
        (datos?.personas || []).forEach((p) => Object.values(p.celdas).forEach((d) => { c[d.estado] = (c[d.estado] || 0) + 1; }));
        return c;
    }, [datos]);

    const descargar = async (tipo) => {
        setBajando(tipo);
        try {
            const ruta = tipo === 'semanal' ? '/api/asistencia/jornadas/semanal' : '/api/asistencia/jornadas';
            const { data } = await axiosInstance.get(ruta, { params: { ...params, formato: 'xlsx' }, responseType: 'blob' });
            const url = URL.createObjectURL(data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `asistencia_${tipo === 'semanal' ? 'por_semana' : 'dia_a_dia'}_${desde}_${hasta}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error('No se pudo descargar el Excel');
        } finally {
            setBajando(null);
        }
    };

    const dias = datos?.dias || [];
    const ancha = dias.length <= 10;
    const anchoDia = ancha ? 84 : 30;
    const columnasTotales = '64px 64px 56px 56px 56px';
    const plantilla = `15rem repeat(${dias.length}, ${anchoDia}px) 296px`;
    const hoy = iso(new Date());

    const titulo = () => {
        const a = aFecha(desde);
        const b = aFecha(hasta);
        const mismoMes = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
        const fmt = (d, conMes) => d.toLocaleDateString('es-VE', conMes ? { day: 'numeric', month: 'long' } : { day: 'numeric' });
        return desde === hasta ? fechaLarga(desde) : `Del ${fmt(a, !mismoMes)} al ${fmt(b, true)}`;
    };

    const ordenarPor = (k) => setOrden((o) => (o === k ? 'nombre' : k));
    const Cabecera = ({ k, children, titulo: t }) => (
        <button type="button" onClick={() => ordenarPor(k)} title={t}
            className={`px-2 py-2 text-right text-xs font-medium hover:text-slate-900 focus-visible:outline-none focus-visible:underline ${orden === k ? 'text-sura-blue' : 'text-slate-500'}`}>
            {children}
        </button>
    );

    return (
        <div className="space-y-5">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className={`${display} text-3xl font-bold text-slate-900`}>{titulo()}</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {datos ? `${datos.personas.length} personas. Toca a alguien para ver su mes completo.` : 'Armando la planilla...'}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button type="button" onClick={() => descargar('diario')} disabled={!!bajando || !datos} className={botonClaro}>
                        {bajando === 'diario' ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Excel día a día
                    </button>
                    <button type="button" onClick={() => descargar('semanal')} disabled={!!bajando || !datos} className={botonClaro}>
                        {bajando === 'semanal' ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Excel por semana
                    </button>
                </div>
            </header>

            <div className="flex flex-wrap items-center gap-3">
                <Segmentado opciones={PERIODOS} valor={clave} onCambio={elegirPeriodo} etiqueta="Período" />
                <div className="flex items-center gap-2">
                    <input type="date" value={desde} onChange={(e) => cambiarFecha(0, e.target.value)} className={campo} aria-label="Desde" />
                    <span className="text-sm text-slate-400">al</span>
                    <input type="date" value={hasta} onChange={(e) => cambiarFecha(1, e.target.value)} className={campo} aria-label="Hasta" />
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
                <select value={sedeId} onChange={(e) => setSedeId(e.target.value)} className={campo} aria-label="Sede">
                    <option value="">Todas las sedes</option>
                    {filtros.sedes.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                </select>
                <select value={deptoId} onChange={(e) => setDeptoId(e.target.value)} className={`${campo} max-w-[16rem]`} aria-label="Departamento">
                    <option value="">Todos los departamentos</option>
                    {filtros.departamentos.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                </select>
                <div className="relative flex-1 min-w-[12rem] max-w-sm">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="search" value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Buscar persona o cédula" className={`${campo} w-full pl-9`} />
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                    <input type="checkbox" checked={soloNovedades} onChange={(e) => setSoloNovedades(e.target.checked)} className="rounded border-slate-300 text-cyan-500 focus:ring-cyan-400/50" />
                    Solo quien tuvo retardos o faltas
                </label>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                <Leyenda conteo={conteo} estados={LEYENDA.filter((e) => e !== 'libre' || conteo.libre)} />
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                    <span className="w-3 h-3 rounded-full bg-cyan-400" />
                    Horas extra
                </span>
            </div>
            {error && <p className="text-sm text-rose-600">{error}</p>}

            {!datos ? <Cargando alto="h-96" /> : (
                <div className={`${panel} relative overflow-auto scroll-fino max-h-[calc(100vh-19rem)] min-h-[20rem] ${cargando ? 'opacity-60' : ''} transition-opacity`}>
                    {personas.length === 0 ? (
                        <p className="p-12 text-center text-sm text-slate-500">
                            {soloNovedades ? 'Nadie tuvo retardos ni faltas en este período.' : 'Nadie con estos filtros.'}
                        </p>
                    ) : (
                        <div className="grid w-max min-w-full" style={{ gridTemplateColumns: plantilla }}>
                            {/* Cabecera */}
                            <div className="sticky top-0 left-0 z-30 bg-white border-b border-slate-200 px-4 py-2 flex items-end">
                                <button type="button" onClick={() => setOrden('nombre')}
                                    className={`text-xs font-medium ${orden === 'nombre' ? 'text-sura-blue' : 'text-slate-500'} hover:text-slate-900`}>
                                    Persona
                                </button>
                            </div>
                            {dias.map((d) => {
                                const f = aFecha(d);
                                const feriado = datos.feriados[d];
                                const finde = diaSemana(f) >= 5;
                                return (
                                    <div key={d} title={feriado || undefined}
                                        className={`sticky top-0 z-20 border-b border-slate-200 py-2 flex flex-col items-center justify-end ${
                                            d === hoy ? 'bg-cyan-50' : feriado || finde ? 'bg-slate-50' : 'bg-white'}`}>
                                        <span className={`text-[10px] ${feriado ? 'text-rose-400' : 'text-slate-400'}`}>{DIAS_LETRA[diaSemana(f)]}</span>
                                        <span className={`text-xs tabular-nums font-medium ${d === hoy ? 'text-cyan-700' : finde || feriado ? 'text-slate-400' : 'text-slate-700'}`}>{f.getDate()}</span>
                                    </div>
                                );
                            })}
                            <div className="sticky top-0 right-0 z-30 bg-white border-b border-l border-slate-200 grid items-end" style={{ gridTemplateColumns: columnasTotales }}>
                                <Cabecera k="horas" titulo="Horas trabajadas dentro del horario, sin el almuerzo">Horas</Cabecera>
                                <span className="px-2 py-2 text-right text-xs font-medium text-slate-400" title="Horas que tocaban según el horario">Horario</span>
                                <Cabecera k="extra" titulo="Horas extra después de la salida: van aparte, no están en Horas">Extra</Cabecera>
                                <Cabecera k="retardos" titulo="Días con retardo (ordena por minutos de retardo)">Tarde</Cabecera>
                                <Cabecera k="ausencias" titulo="Días que faltó">Faltas</Cabecera>
                            </div>

                            {personas.map((p) => (
                                <Fila
                                    key={p.empleado_id} persona={p} dias={dias} ancha={ancha} columnas={columnasTotales}
                                    onEncima={(e, persona, d) => {
                                        const r = e.currentTarget.getBoundingClientRect();
                                        setEncima({ persona, d, celda: persona.celdas[d], x: r.left + r.width / 2, y: r.top });
                                    }}
                                    onSalir={() => setEncima(null)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Detalle del día bajo el puntero */}
            {encima?.celda && (
                <Flotante>
                <div className="fixed z-50 pointer-events-none -translate-x-1/2 -translate-y-full -mt-2 px-3 py-2 rounded-xl bg-slate-900 text-white shadow-lg max-w-xs"
                    style={{ left: encima.x, top: encima.y - 6 }}>
                    <p className="text-xs text-slate-400">{fechaLarga(encima.d)}</p>
                    <p className="text-sm font-medium">{encima.persona.nombre}</p>
                    <p className="text-xs mt-0.5">
                        <span className="text-slate-300">{ESTADOS[encima.celda.estado]?.nombre}</span>
                        {encima.celda.retardo_min > 0 && <span className="text-amber-300">, {minutosTexto(encima.celda.retardo_min)} tarde</span>}
                        {encima.celda.salida_temprana_min > 0 && <span className="text-amber-200">, salió {minutosTexto(encima.celda.salida_temprana_min)} antes</span>}
                    </p>
                    {textoCelda(encima.celda) && encima.celda.estado !== 'justificada' && encima.celda.estado !== 'libre' && (
                        <p className="text-xs text-slate-300">{textoCelda(encima.celda)}{encima.celda.horas != null && `. ${horas(encima.celda.horas)} h`}</p>
                    )}
                    {encima.celda.extra_min > 0 && (
                        <p className="text-xs text-cyan-300">{minutosTexto(encima.celda.extra_min)} extra, aparte de las horas</p>
                    )}
                    {encima.celda.justificacion && <p className="text-xs text-emerald-300">{encima.celda.justificacion}</p>}
                    {encima.celda.aviso && <p className="text-xs text-rose-300">{encima.celda.aviso}</p>}
                </div>
                </Flotante>
            )}
        </div>
    );
};

export default PeriodoVista;
